import getApi from '@/api/api';
import utilsStorage from '@/utils/storage';

/**
 * Streams AI assistant chat response via fetch + ReadableStream (SSE).
 * @param {Object} params - { message, conversationHistory, context }
 * @param {Object} callbacks - { onToken, onSources, onError, onDone, abortController }
 * @returns {Promise<void>}
 */
async function streamChat({ message, conversationHistory, context, sessionId }, { onToken, onSources, onToolCall, onLastCustomer, onContextChange, onActions, onError, onDone, abortController }) {
  const api = await getApi();
  const baseURL = api.defaults.baseURL;
  const token = await utilsStorage.getTokenOnStorage();
  const user = await utilsStorage.getUserOnStorage();

  const profilePrefix = user?.profileId === 1 ? '/admin'
    : user?.correspondentId ? '/corban'
      : user?.customerId ? '/customer'
        : '/admin';

  const url = `${baseURL}${profilePrefix}/ai-assistant/chat`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token,
      'Accept': 'text/event-stream',
    },
    body: JSON.stringify({ message, conversationHistory, context, sessionId }),
    signal: abortController?.signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    onError(errorData?.message || 'Não foi possível conectar com o assistente. Tente novamente em alguns instantes; se continuar, fale com o suporte.');
    onDone();

    return;
  }

  if (!response.body) {
    onError('Streaming nao suportado neste ambiente. Tente novamente.');
    onDone();

    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();

    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop();

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;

      const data = line.slice(6).trim();

      if (data === '[DONE]') {
        onDone();

        return;
      }

      try {
        const parsed = JSON.parse(data);

        if (parsed.token) onToken(parsed.token);

        if (parsed.sources) onSources(parsed.sources, parsed.conversationId);

        if (parsed.toolCall && onToolCall) onToolCall(parsed.toolCall);

        if (parsed.lastCustomer && onLastCustomer) onLastCustomer(parsed.lastCustomer);

        // `contextChange: null` significa "o servidor descartou o contexto",
        // entao o teste e pela CHAVE, nao pelo valor.
        if ('contextChange' in parsed && onContextChange) onContextChange(parsed.contextChange);

        // Acoes rapidas sugeridas pelo servidor: viram botoes sob a resposta.
        if (parsed.actions && onActions) onActions(parsed.actions);

        if (parsed.error) onError(parsed.error);
      } catch (e) { // eslint-disable-next-line no-empty
      }
    }
  }

  onDone();
}

/**
 * Envia feedback (positivo ou negativo) para uma resposta da IA.
 * @param {number} conversationId - ID da conversa no banco
 * @param {string} feedback - 'positive' ou 'negative'
 * @returns {Promise<Object>}
 */
async function sendFeedback(conversationId, feedback) {
  const api = await getApi();
  const token = await utilsStorage.getTokenOnStorage();
  const user = await utilsStorage.getUserOnStorage();

  const profilePrefix = user?.profileId === 1 ? '/admin'
    : user?.correspondentId ? '/corban'
      : user?.customerId ? '/customer'
        : '/admin';

  const response = await api.patch(`${profilePrefix}/ai-assistant/feedback`, {
    conversationId,
    feedback,
  }, {
    headers: { Authorization: token },
  });

  return response.data;
}


/* O prefixo por perfil e o mesmo do chat: extraido para nao repetir a cascata. */
function profilePrefixFor(user) {
  return user?.profileId === 1 ? '/admin'
    : user?.correspondentId ? '/corban'
      : user?.customerId ? '/customer'
        : '/admin';
}

/**
 * Historico de conversas do operador logado (agrupado por sessionId).
 * @returns {Promise<Array>} [{ sessionId, titulo, turnos, ultimaMensagemEm }]
 */
async function listConversations() {
  const api = await getApi();
  const token = await utilsStorage.getTokenOnStorage();
  const user = await utilsStorage.getUserOnStorage();

  const response = await api.get(`${profilePrefixFor(user)}/ai-assistant/conversations`, {
    headers: { Authorization: token },
  });

  return response.data?.conversations || [];
}

/**
 * Turnos de uma conversa do historico, em ordem cronologica.
 * @param {string} sessionId
 * @returns {Promise<Array>} [{ id, pergunta, resposta, status, feedback, em }]
 */
async function getConversation(sessionId) {
  const api = await getApi();
  const token = await utilsStorage.getTokenOnStorage();
  const user = await utilsStorage.getUserOnStorage();

  const response = await api.get(
    `${profilePrefixFor(user)}/ai-assistant/conversations/${encodeURIComponent(sessionId)}`,
    { headers: { Authorization: token } }
  );

  return response.data?.turns || [];
}

export default { streamChat, sendFeedback, listConversations, getConversation };
